import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useSpring } from 'framer-motion';

const ASSET_BASE = 'https://processed.portal.soot.com/playground-static-assets/';

const Title = ({ children, scale = 1 }) => (
  <svg style={{ overflow: 'visible' }}>
    <text
      x="0"
      y="0"
      style={{
        fontFamily: "'ABC Diatype Rounded Medium', sans-serif",
        fontSize: `${12 * scale}px`,
        letterSpacing: '-0.02em',
        fill: '#ecff0f',
        stroke: '#666',
        strokeWidth: `${3.5 * scale}px`,
        paintOrder: 'stroke fill',
      }}
    >
      {children}
    </text>
  </svg>
);

const CursorLabel = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [labelText, setLabelText] = useState('');
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });

  const springX = useSpring(0, {
    stiffness: 400,
    damping: 40,
    mass: 0.5
  });

  const springY = useSpring(0, {
    stiffness: 100,
    damping: 20,
    mass: 0.5
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    springX.set(mousePosition.x + 20);
    springY.set(mousePosition.y + 20);
  }, [mousePosition, springX, springY]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleHover = (e) => {
      const target = e.target;

      // Check for image links with data attributes
      const imageLink = target.closest('[data-image-link]');
      if (imageLink) {
        const hoverText = imageLink.dataset.hoverText;
        if (hoverText) {
          setLabelText(hoverText);
          setIsVisible(true);
          return;
        }
      }

      // Handle video and tutorial elements
      if (target.tagName.toLowerCase() === 'video' || target.closest('video') || 
          target.closest('[style*="mask: url(#content-mask)"]')) {
        setLabelText('How to map');
        setIsVisible(true);
        return;
      }

      // Handle invite image
      if (target.src && target.src.includes('admitsone.png')) {
        setLabelText('Invites');
        setIsVisible(true);
        return;
      }

      setIsVisible(false);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseover', handleHover);
    document.addEventListener('mouseout', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleHover);
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, []);

  return (
    <>
      <style>
        {`@font-face {
          font-family: 'ABC Diatype Rounded';
          src: url('${ASSET_BASE}ABCDiatypeRoundedPlusVariable-Trial.ttf') format('truetype');
          font-weight: 100 900;
        }`}
      </style>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="fixed pointer-events-none"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            style={{
              left: springX,
              top: springY,
              zIndex: 9999
            }}
          >
            <Title scale={1.2}>
              {labelText}
            </Title>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CursorLabel;